// Microsoft Entra ID sign-in.
//
// Auth code with PKCE, in the browser. The API validates the token itself
// rather than trusting a gateway header, which is what lets the whole stack
// run on a laptop with the real auth path exercised.
//
// Nothing here is a secret. A client id and a tenant id are public by design;
// the access token is the credential, and it is held by MSAL rather than by
// this code.

import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser'

const TENANT = import.meta.env.VITE_ENTRA_TENANT_ID
const CLIENT = import.meta.env.VITE_ENTRA_CLIENT_ID

// Off by default so `npm run dev` works with no tenant configured. The API's
// AUTH_ENABLED must match: with auth off it attributes every write to the
// configured development identity.
export const authEnabled = String(import.meta.env.VITE_AUTH_ENABLED) === 'true'

// The API's own scope, not Graph's. A Graph token is not valid here: its
// audience is Microsoft, and the API checks the audience.
const API_SCOPE = CLIENT ? `api://${CLIENT}/access_as_user` : null

export const msal = authEnabled
  ? new PublicClientApplication({
      auth: {
        clientId: CLIENT,
        // Single-tenant. `common` would let any Microsoft account reach the
        // sign-in page and be rejected later, which is a worse experience and
        // a wider surface.
        authority: `https://login.microsoftonline.com/${TENANT}`,
        redirectUri: window.location.origin,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        // Survives a refresh; cleared when the browser is closed. A token in
        // localStorage outlives the session for no benefit here.
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
    })
  : null

let ready = null

export async function initAuth() {
  if (!authEnabled) return null
  if (!TENANT || !CLIENT) {
    throw new Error(
      'VITE_ENTRA_TENANT_ID and VITE_ENTRA_CLIENT_ID must be set when VITE_AUTH_ENABLED is true.',
    )
  }
  if (!ready) {
    ready = msal.initialize().then(async () => {
      // Completes a redirect sign-in. Must run before anything reads accounts.
      await msal.handleRedirectPromise()
      const [account] = msal.getAllAccounts()
      if (account) msal.setActiveAccount(account)
      return account || null
    })
  }
  return ready
}

export function activeAccount() {
  return authEnabled ? msal.getActiveAccount() : null
}

// A redirect is a one-way trip, and several can be asked for at once: the
// opening screen makes half a dozen API calls, and a dead session 401s all of
// them. MSAL throws `interaction_in_progress` on the second, which surfaces as
// an error about the configuration rather than as the navigation that is
// already happening. So the first request wins and the rest wait on it.
let leaving = null

export async function signIn() {
  await initAuth()
  // Redirect rather than popup: a popup is blocked often enough that the first
  // impression of the app becomes a browser warning.
  if (!leaving) leaving = msal.loginRedirect({ scopes: [API_SCOPE] })
  return leaving
}

// Signing out is a decision, and the app boots again on the way back from it.
// Without this record that boot would see no account and send the person
// straight to Microsoft -- so signing out would sign them back in, and the
// button would do nothing you could observe.
const SIGNED_OUT = 'gtm.auth.signed-out'

export function signedOutDeliberately() {
  return sessionStorage.getItem(SIGNED_OUT) === '1'
}

export function clearSignedOut() {
  sessionStorage.removeItem(SIGNED_OUT)
}

export async function signOut() {
  await initAuth()
  sessionStorage.setItem(SIGNED_OUT, '1')
  return msal.logoutRedirect({ postLogoutRedirectUri: window.location.origin })
}

// The bearer token for an API call. Silent when the cached token is still good,
// which is the normal path; a redirect only when the refresh token has expired.
export async function accessToken() {
  if (!authEnabled) return null
  await initAuth()
  const account = msal.getActiveAccount()
  if (!account) return null
  try {
    const result = await msal.acquireTokenSilent({ scopes: [API_SCOPE], account })
    return result.accessToken
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Same one-way trip, same guard: parallel calls all hit this together.
      if (!leaving) leaving = msal.acquireTokenRedirect({ scopes: [API_SCOPE], account })
      await leaving
      return null
    }
    throw err
  }
}
